import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, LogOut, UserCircle, Film, Sun, Moon } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useAuthStore } from '@/stores/authStore';
import { useAppStore } from '@/stores/appStore';
import { PointBalance } from './PointBalance';

export function Header() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const theme = useAppStore((s) => s.theme);
  const toggleTheme = useAppStore((s) => s.toggleTheme);
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <header className="h-14 bg-background sticky top-0 z-40 flex items-center px-4 shrink-0">
      <div className="flex items-center gap-3 min-w-fit w-[144px]">
        <Link to="/home" className="flex items-center gap-2.5">
          <img
            src="/logo-wizmage-32.png"
            alt="WizMage"
            className="w-8 h-8 rounded-lg shrink-0"
          />
          <span className="text-base font-bold text-text-primary hidden sm:inline whitespace-nowrap">
            WizMage
          </span>
        </Link>
      </div>

      <div className="flex items-center gap-3 justify-end relative z-50 ml-auto">
        <button
          onClick={() => setSearchOpen(true)}
          className="p-2 rounded-lg text-muted hover:bg-surface-hover hover:text-text-primary transition-colors"
          aria-label="搜索"
        >
          <Search size={18} />
        </button>

        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg text-muted hover:bg-surface-hover hover:text-text-primary transition-colors"
          aria-label={theme === 'dark' ? '切换到浅色模式' : '切换到深色模式'}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Point Balance */}
        <PointBalance className="hidden sm:inline-flex" />

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="flex items-center gap-2 p-0.5 rounded-full hover:bg-surface-hover transition-colors">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary-light flex items-center justify-center text-primary-foreground text-sm font-medium">
                {user?.nickname?.charAt(0) || user?.phone?.charAt(0) || 'U'}
              </div>
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              sideOffset={6}
              align="end"
              className="min-w-[180px] bg-surface border border-border rounded-xl shadow-xl shadow-overlay/30 p-1.5 z-50 animate-in fade-in zoom-in-95 duration-150"
            >
              <div className="px-3 py-2 border-b border-border mb-1">
                <p className="text-sm font-medium text-text-primary">
                  {user?.nickname || '用户'}
                </p>
                <p className="text-xs text-muted">
                  {user?.phone || ''}
                </p>
              </div>

              <DropdownMenu.Item asChild>
                <Link
                  to="/profile"
                  className="flex items-center gap-2.5 px-3 py-2 text-sm text-muted rounded-lg hover:bg-surface-hover hover:text-text-primary outline-none cursor-pointer transition-colors"
                >
                  <UserCircle size={16} />
                  个人中心
                </Link>
              </DropdownMenu.Item>

              <DropdownMenu.Item asChild>
                <Link
                  to="/home"
                  className="flex items-center gap-2.5 px-3 py-2 text-sm text-muted rounded-lg hover:bg-surface-hover hover:text-text-primary outline-none cursor-pointer transition-colors"
                >
                  <Film size={16} />
                  我的作品
                </Link>
              </DropdownMenu.Item>

              <DropdownMenu.Separator className="h-px bg-border my-1" />

              <DropdownMenu.Item
                onClick={logout}
                className="flex items-center gap-2.5 px-3 py-2 text-sm text-error rounded-lg hover:bg-error/10 outline-none cursor-pointer transition-colors"
              >
                <LogOut size={16} />
                退出登录
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      {searchOpen && (
        <div
          className="fixed inset-0 z-50 bg-overlay/70 flex items-start justify-center pt-[15vh]"
          onClick={() => setSearchOpen(false)}
        >
          <div
            className="w-full max-w-xl mx-4 bg-surface border border-border rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
              <Search size={18} className="text-muted" />
              <input
                type="text"
                placeholder="搜索..."
                autoFocus
                className="flex-1 bg-transparent text-text-primary text-sm placeholder:text-muted outline-none"
              />
              <button
                onClick={() => setSearchOpen(false)}
                className="text-xs text-muted hover:text-text-primary px-2 py-1 rounded border border-border"
              >
                ESC
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
